import React from "react";

import { Check, FileText, FilePlus } from "lucide-react";

import TabsComponent from "../../../../Components/Tab";

interface Step1Props {
  templateTab: string;
  templates: any[];
  loading: boolean;
  selectedTemplateId: string;
  onTemplateTabChange: (tabId: string) => void;
  onSelectTemplate: (template: any) => void;
}

const Step1TemplateSelection: React.FC<Step1Props> = ({
  templateTab,
  templates,
  loading,
  selectedTemplateId,
  onTemplateTabChange,
  onSelectTemplate,
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Choose Assessment Template
        </h3>
        <p className="text-gray-600">
          Select a template that best fits your assessment needs
        </p>
      </div>

      <TabsComponent
        tabs={[
          {
            id: "izba_provided",
            label: "Izba Provided",
            icon: <FileText className="h-4 w-4 mr-2" />,
          },
          {
            id: "custom",
            label: "Custom",
            icon: <FilePlus className="h-4 w-4 mr-2" />,
          },
        ]}
        activeTab={templateTab}
        onTabChange={onTemplateTabChange}
        variant="tab-underline"
        className="border-b"
      />

      <div>
        {loading ? (
          <div className="w-full md:w-1/span-full text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="w-full md:w-1/span-full text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No templates available
            </h3>
            <p className="text-gray-600">
              Please contact your administrator to set up assessment templates
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
            {templates.map(template => (
              <div
                key={template.id}
                onClick={() => onSelectTemplate(template)}
                className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedTemplateId === template.id
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:border-purple-300"
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-semibold text-gray-900 text-lg">
                    {template.name}
                  </h4>
                  {selectedTemplateId === template.id && (
                    <Check className="h-5 w-5 text-purple-600" />
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  {template.description}
                </p>
                <div className="flex items-center justify-between text-sm">
                  <span
                    className={`px-2 py-1 rounded-full ${
                      template.category === "short"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-purple-100 text-purple-800"
                    }`}
                  >
                    {template.category === "short"
                      ? "Short Assessment"
                      : "Long Assessment"}
                  </span>
                  <span className="text-gray-500">
                    {template.questions.length} questions
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Step1TemplateSelection;
